
import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import { observer } from "mobx-react";
import { useSnackbar } from "notistack";

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, CircularProgress, ownerDocument, setRef, useTheme } from "@mui/material";

import { mainListItems } from "./DashboardListItems";
import { AppBarStyled, CollapsibleDrawer, Root, ToolbarIcon } from "./Styles";

import CreateGoogleSheet from "./components/CreateGoogleSheet";
import SplitButton from "./components/SplitButton";

import DialogAbout from "./dialogs/DialogAbout";
import DialogProjectName from "./dialogs/DialogProjectName";
import DialogOpenProject from "./dialogs/DialogOpenProject";

import DialogOpenProjectWithFolders from "./dialogs/DialogOpenProjectWithFolders";
import DialogCreateNewProject from "./dialogs/DialogCreateNewProject";

import ProjectStore, { useProjectStore } from "./model/ProjectStore";
import { checkHash, startProjectDownload, getDownloadStatus } from "./operations/ProjectOperations";
import { ReactComponent as Logo } from "./resources/EVA_icon.svg";

import ManageProjectView from "./views/ManageProjectView";
import SummaryView from "./views/SummaryView";
import TaskView from "./views/TaskView";
import ConfigureDistributionView from "./views/ConfigureDistributionView";
import AnalysisView from "./views/AnalysisView";
import ManageBaselineView from "./views/ManageBaselineView";

import Alert from '@mui/material/Alert';
import CssBaseline from "@mui/material/CssBaseline";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";

import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import SaveIcon from "@mui/icons-material/Save";
import OpenIcon from "@mui/icons-material/FolderOpen";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import RefreshIcon from "@mui/icons-material/Refresh";
import NoteAddIcon from "@mui/icons-material/NoteAdd";

import { useViewState } from "./model/ViewState";
import { runInAction } from "mobx";

import CreateImportTemplate from "./components/CreateImportTemplate";
import ImportFromGoogleSheets from "./components/ImportFromGoogleSheets";

import { getUserName, updateGoogleTokens } from "./operations/AccountOperations"

import ImportWBSFromXLSX from "./components/ImportWBSFromXLSX";
import ImportActualsFromXLSX from "./components/ImportActualsFromXLSX";
import ExportActualsToXLSX from "./components/ExportActualsToXLSX";

import ExportEVAToXLSX from "./components/ExportEVAToXLSX";


let contentKey = 1;

const Dashboard = () => {
  const theme = useTheme();
  const store = useProjectStore();
  const view = useViewState();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const projectName = store.name !== "" ? store.name : "New Project";
  // const [isRefreshing, setIsRefreshing] = useState(false);
  // const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // checkHash(store.id, store.currentHash, enqueueSnackbar).catch(
    //   (error) => {
    //     enqueueSnackbar(error.message, { variant: "error" });
    //     if (error.cause === "opaqueredirect") {
    //       setTimeout(() => {
    //         document.location.reload();
    //       }, 1000);
    //     }
    //   });

    getUserName((res) => {
      view.setUsername(res);
      if (!(process.env.NODE_ENV !== "production")) {
        updateGoogleTokens();
      }
    }, enqueueSnackbar
    );
    
  }, []);

  useEffect(() => {
    if (store.id === "none") {
      view.setLoadProjectDialogState(true);
    }
  }, [store.id]);

  const saveToCloud = () => {
    if (
      store.name === "" ||
      store.name == null
    ) {
      view.setSaveDialogState(true);
      return;
    }
    // this.setState({ saving: true });
    setIsSaving(true);
    const saveState = store.toJS();
    let saveAPI = "/api/projects/save/" + store.id;
    if (process.env.NODE_ENV !== "production") {
      saveAPI = "http://localhost:7071" + saveAPI;
    }
    fetch(saveAPI, {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ Projectdata: JSON.stringify(saveState) }),
      redirect: "manual",
    })
      .then((response) => {
        if (response.type === "opaqueredirect") {
          enqueueSnackbar(
            "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
            { variant: "error" }
          );
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
          return;
        }
        if (!response.ok) {
          response
            .text()
            .then((text) => {
              throw Error(response.statusText + ": " + text);
            })
            .catch((error) =>
              enqueueSnackbar(error.message, { variant: "error" })
            );
        } else {
          response.json().then((result) => {
            if (result.currentHash !== -1) {
              // let newProject = ProjectStore.fromJS(result);
              // this.setState({ projectStore: newProject });
              store.updateStore(result);
            } else {
              console.log(`Is snackbar supposed to open here?`);
              // this.setState({ open: true });
              // view.open()
            }

            setIsSaving(false);
            enqueueSnackbar("Project is successfully saved!", { variant: "success" })
          });
        }
      })
      .catch((error) =>
        enqueueSnackbar(error.message, { variant: "error" })
      );
  };

  const loadFromCloud = (projectId) => {
    // setIsRefreshing(true);
    view.setProjectIsLoading(true);
    let getAPI =
      "/api/project/" + projectId;
    if (process.env.NODE_ENV !== "production") {
      getAPI = "http://localhost:7071" + getAPI;
    }
    fetch(getAPI, {
      method: "GET",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      redirect: "manual",
    })
      .then((response) => {
        if (response.type === "opaqueredirect") {
          enqueueSnackbar(
            "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
            { variant: "error" }
          );
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
          return;
        }
        if (!response.ok) {
          response
            .text()
            .then((text) => {
              throw Error(response.statusText + ": " + text);
            })
            .catch((error) =>
              enqueueSnackbar(error.message, { variant: "error" })
            );
        } else {
          response.json().then((result) => {
            store.dispose();
            store.updateStore(result);
            // contentKey = contentKey + 1;
            // setIsRefreshing(false);
            view.setProjectIsLoading(false);
            // setRefreshSuccess(true);
            enqueueSnackbar("The latest project data has been loaded!", {
              variant: "success",
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "center",
              },
            })
          });
        }
      })
      .catch((error) =>
        enqueueSnackbar(error.message + { variant: "error" })
      );
  };

  const handleProjectDownload = () => {
    startProjectDownload(store.id, (startResult) => {
      enqueueSnackbar("Please wait as the download has started. This could take a few minutes", {
        variant: "info",
        persist: true,
        // action: (id) =>
        //   <IconButton onClick={() => closeSnackbar(id)} color="inherit">
        //     <CloseIcon />
        //   </IconButton>,
      });

      const pollServer = () => {
        getDownloadStatus(startResult.instanceId, (statusResult) => {
          if (statusResult.completed === true) {
            const urlString = statusResult.output;
            enqueueSnackbar("Here is the Link to the download: ", {
              variant: "info",
              persist: true,
              // action: (id) =>
              //   <IconButton onClick={() => closeSnackbar(id)} color="inherit">
              //     <CloseIcon />
              //   </IconButton>,
            });
            enqueueSnackbar(<a href={urlString}>{urlString}</a>, {
              variant: "info",
              persist: true,
              // action: (id) =>
              //   <IconButton onClick={() => closeSnackbar(id)} color="inherit">
              //     <CloseIcon />
              //   </IconButton>,
            });
          } else {
            setTimeout(pollServer, 10000);
          }
        }).catch((error) =>
          enqueueSnackbar(
            "Project failed to export and the download could not be completed. " +
            error.message,
            { variant: "error" },
          ));
      }

      setTimeout(pollServer, 10000);
    }).catch((error) =>
      enqueueSnackbar(
        "Failed to start project download. " +
        error.message,
        { variant: "error" },
      ));
  }


  const handleCreateNewProject = useCallback(async (name, isCloning, projectToClone) => {
    view.setNewProjectDialogState(false);

    let newProjectData;
    if (isCloning && projectToClone) {

      view.setProjectIsLoading(true);
      const encodedName = encodeURIComponent(name);
      let cloneAPI = `/api/project/clone/${projectToClone.id}/${encodedName}`;
      if (process.env.NODE_ENV !== "production") {
        cloneAPI = "http://localhost:7071" + cloneAPI;
      }

      try {
        const response = await fetch(cloneAPI, {
          method: "POST",
          headers: {
            Accept: "application/json, text/plain, */*",
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          let errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorText = errorJson.Error || errorText;
          } catch (e) { /* ignore if not JSON */ }

          throw new Error(`Clone failed (${response.status} ${response.statusText}): ${errorText}`);
        }

        const result = await response.json();
        const newProjectId = result.NewProjectId; 

        enqueueSnackbar(`Project successfully cloned as "${name}"! Loading new project...`, { 
          variant: "success" 
        });

        loadFromCloud(newProjectId);

      } catch (error) {
        enqueueSnackbar(`Cloning Error: ${error.message}`, { variant: "error" });
      } finally {
        view.setProjectIsLoading(false);
      }
    } else {
      alert("not implemented");
    }
  }, []);

  const handleOpenExistingProject = (folderName, file) => {
    loadFromCloud(file.id);
    view.setLoadProjectDialogState(false);
  }

  const handleRefreshProject = () => {
    loadFromCloud(store.id);
  }

  const handleSaveProjectName = (name) => {
    // this.setState(
    //   (prevState) => ({
    //     projectStore: {
    //       ...prevState.projectStore,
    //       name: name,
    //     },
    //     saveDialogOpen: false,
    //   }),
    runInAction(() => {
      store.name = name;
    })
    saveToCloud()
    view.setSaveDialogState(false);
    // );
  };

  // const handleDrawerOpen = useCallback(() => {
  //   view.mainDrawerOpen = true;
  // }, [view]);

  // const handleDrawerClose = useCallback(() => {
  //   view.mainDrawerOpen = false;
  // }, [view]);

  const handleClose = useCallback(() => {
    view.open = false;
  }, [view]);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    // TODO: saving and redefining
    // setIsRefreshing(false);
    // setRefreshSuccess(false);
  }

  return (
    <Root aria-hidden="false">

      {/* Toasts */}
      <Snackbar
        open={view.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity="error"
          elevation={6}
          variant="filled"
        >
          Current project version is outdated. Please load the most current version of project, changes not saved.
        </Alert>
      </Snackbar>

      {/* <Snackbar
        open={refreshSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          The latest project data has been loaded!
        </Alert>
      </Snackbar> */}

      {/* Dialogs */}
      <Dialog open={view.projectIsLoading || isSaving}>
        <DialogContent style={{ backgroundColor: 'rgb(232, 244, 253)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} />
            <span style={{ marginLeft: 16 }}>{isSaving ? "Saving..." : "Refreshing..."}</span>
          </div>
        </DialogContent>
      </Dialog>
      <DialogAbout
        open={view.aboutDialogOpen}
        onClose={() => view.setAboutDialogState(false)}
      />
      <DialogCreateNewProject
        open={view.newProjectDialogOpen}
        onSave={handleCreateNewProject}
        onCancel={() => view.setNewProjectDialogState(false)}
      />
      <DialogOpenProject
        open={false}
        onFinish={handleOpenExistingProject}
        onCancel={() => view.setLoadProjectDialogState(false)}
      />
      <DialogOpenProjectWithFolders
        open={view.loadProjectDialogOpen}
        onFinish={handleOpenExistingProject}
        onCancel={() => view.setLoadProjectDialogState(false)}
      />
      <DialogProjectName
        open={view.saveDialogOpen}
        onSave={handleSaveProjectName}
        onCancel={() => view.setSaveDialogState(false)}
      />

      {/* Dashboard UI starts here */}
      <Router>
        <CssBaseline />
        <AppBarStyled
          position="absolute"
          open={view.mainDrawerOpen}
          aria-hidden="false"
        >
          <Toolbar
            disableGutters={!view.mainDrawerOpen}
            sx={{
              paddingRight: 3,
              paddingLeft: view.mainDrawerOpen ? 0 : "12px"
            }}
          >
            <Logo
              color="inherit"
              style={{
                height: "30px",
                width: "30px",
                minWidth: "30px",
                minHeight: "30px",
                margin: "0px 15px 0px 0px"
              }}
              onClick={() => view.setMainDrawerState(true)}
            />
            <Typography
              component="h1"
              variant="h6"
              color="inherit"
              noWrap
              sx={{ flexGrow: 1 }}
            >
              {projectName}
            </Typography>

            <Tooltip title="New Project" aria-label="New">
              <IconButton
                color="inherit"
                onClick={() => view.setNewProjectDialogState(true)}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open Project" aria-label="Open">
              <IconButton
                color="inherit"
                onClick={() => view.setLoadProjectDialogState(true)}
              >
                <OpenIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Save Project" aria-label="Save">
              <IconButton
                color="inherit"
                onClick={saveToCloud}
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh" aria-label="Refresh">
              <IconButton
                color="inherit"
                onClick={handleRefreshProject}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <SplitButton
              options={[
                {
                  menuIcon: <NoteAddIcon />,
                  label: "Export EVA",
                  component: <ExportEVAToXLSX />,
                  onClick: null,
                },
                {
                  menuIcon: <CloudDownloadIcon />,
                  label: "Download",
                  component: (
                    <Tooltip title="Download" aria-label="Download">
                      <IconButton color="inherit">
                        <CloudDownloadIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                  onClick: () => handleProjectDownload()
                },
                {
                  menuIcon: <NoteAddIcon />,
                  label: "Import WBS Updates",
                  component: <ImportWBSFromXLSX />,
                  onClick: null,
                }, 
                {
                  menuIcon: <NoteAddIcon />,
                  label: "Import Actuals from Excel",
                  component: <ImportActualsFromXLSX />,
                  onClick: null,
                }, 
                {
                  menuIcon: <NoteAddIcon />,
                  label: "Export Actuals to Excel",
                  component: <ExportActualsToXLSX />,
                  onClick: null,
                }, 
                {
                  menuIcon: <NoteAddIcon />,
                  label: "Import Actuals from Google",
                  component: <ImportFromGoogleSheets/>,
                  onClick: null,
                }
              ]}
            />
            <Tooltip title="About" aria-label="About">
              <IconButton
                color="inherit"
                onClick={() => view.setAboutDialogState(true)}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign Out" aria-label="Logout">
              <Button
                color="inherit"
                variant="outlined"
                href="/.auth/logout"
              >
                {view.userName}
              </Button>
            </Tooltip>
          </Toolbar>
        </AppBarStyled>

        {/* Left panel */}
        <Drawer
          variant="permanent"
          open={view.mainDrawerOpen}
          sx={{
            '& .MuiDrawer-paper': {
              position: "relative",
              height: "100vh",
              whiteSpace: "nowrap",
              width: view.mainDrawerOpen ? 240 : theme.spacing(7),
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: view.mainDrawerOpen
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
              }),
              overflowX: view.mainDrawerOpen ? "auto" : "hidden",
              [theme.breakpoints.up("sm")]: {
                width: view.mainDrawerOpen ? 240 : theme.spacing(9),
              },
            },
          }}
        >
          <ToolbarIcon>
            <IconButton onClick={() => view.setMainDrawerState(false)}>
              <ChevronLeftIcon />
            </IconButton>
          </ToolbarIcon>
          <Divider />
          <List>{mainListItems}</List>
        </Drawer>

        <main
          style={{
            flexGrow: 1,
            padding: theme.spacing(3),
            height: "100vh",
            overflow: "auto",
          }}
        >
          <div style={theme.mixins.toolbar} />
          <div style={{ height: 320 }}>
            <Routes>
              <Route
                path="/summary"
                element={<SummaryView />}
              />
              <Route
                path="/tasks"
                element={<TaskView />}
              />
              <Route
                path="/distributions"
                element={<ConfigureDistributionView />}
              />
              <Route
                path="/analysis"
                element={<AnalysisView />}
              />
              <Route
                path="/baselines"
                element={<ManageBaselineView />}
              />
              <Route
                path="/projects"
                element={<ManageProjectView />}
              />
            </Routes>
          </div>
        </main>
      </Router>
    </Root>
  );
}

// Dashboard.propTypes = {
//   projectStore: PropTypes.object.isRequired,
//   viewState: PropTypes.object.isRequired,
// };

export default observer(Dashboard);
// export default withStyles(styles, { withTheme: true })(observer(Dashboard));

// class Dashboard extends React.Component {
//   constructor(props) {
//     super(props);

//     this.state = initialViewState();
//     this.state.projectStore = this.props.projectStore;
//     this.state.userName = "";
//     this.viewState = this.props.viewState;
//     this.state.open = false;
//   }

//   createNewProject = (projectName, callback) => { };

//   handleDrawerOpen = () => {
//     this.setState({ mainDrawerOpen: true });
//   };

//   handleDrawerClose = () => {
//     this.setState({ mainDrawerOpen: false });
//   };

//   handleSaveDialogClose = () => {
//     this.setState({ saveDialogOpen: false });
//   };

//   handleSaveProjectName = (name) => {
//     this.setState(
//       (prevState) => ({
//         projectStore: {
//           ...prevState.projectStore,
//           name: name,
//         },
//         saveDialogOpen: false,
//       }),
//       this.saveToCloud()
//     );
//   };

//   handleOpenProject = (folderName, file) => {
//     this.loadFromCloud(file);
//     this.setState({ projectDialogOpen: false });
//   };

//   handleOpenProjectDialogClose = () => {
//     this.setState({ projectDialogOpen: false });
//   };

//   handleClose = (event, reason) => {
//     this.setState({ open: false });
//   };

//   handleAboutDialogClose = () => {
//     this.setState({ aboutDialogOpen: false });
//   };

//   handleCreateNewProjectDialogClose = () => {
//     this.setState({ createNewProjectDialogOpen: false });
//   };

//   handleCreateNewProjectDialogOpen = () => {
//     this.setState({ createNewProjectDialogOpen: true });
//   };

//   handleCreateNewProjectDialogSave = (projectName) => {
//     this.setState({
//       projectStore: ProjectStore.EmptyProjectStore(
//         projectName,
//         this.state.userName
//       ),
//       createNewProjectDialogOpen: false,
//     });

//     setTimeout(() => {
//       this.saveNewProjToCloud();
//     }, 1000);
//   };

//   handleCloseSnackbar = (event, reason) => {
//     if (reason === "clickaway") {
//       return;
//     }
//     this.setState({ saving: false });
//     this.setState({ savingSuccess: false });
//     this.setState({ refreshing: false });
//     this.setState({ refreshingSuccess: false });
//     this.setState({ redefining: false });
//     this.setState({ redefiningSuccess: false });
//   };

//   handleCloseNetErr = () => {
//     this.setState({ openNetErr: false });
//   };

//   handleReloadNetErr = () => {
//     document.location.reload();
//     this.setState({ openNetErr: false });
//   };

//   handleRedefine = () => {
//     this.copyIdToDispInd(
//       this.state.projectStore.rootTaskPackage,
//       this.state.projectStore
//     );
//     setTimeout(() => {
//       this.redefineTaskAndPkgIds();
//     }, 1000);
//   };

//   handleProjectDownload = () => {
//     startProjectDownload(this.state.projectStore.id, (startResult) => {
//       this.props.enqueueSnackbar("Please wait as the download has started. \
//         This could take a few minutes", {
//         variant: "info",
//         persist: true,
//         action: (id) =>
//           <IconButton onClick={() => this.props.closeSnackbar(id)} color="inherit">
//             <CloseIcon />
//           </IconButton>,
//       });

//       const pollServer = () => {
//         getDownloadStatus(startResult.instanceId, (statusResult) => {
//           if (statusResult.completed === true) {
//             const urlString = statusResult.output;
//             this.props.enqueueSnackbar("Here is the Link to the download: ", {
//               variant: "info",
//               persist: true,
//               action: (id) =>
//                 <IconButton onClick={() => this.props.closeSnackbar(id)} color="inherit">
//                   <CloseIcon />
//                 </IconButton>,
//             });
//             this.props.enqueueSnackbar(<a href={urlString}>{urlString}</a>, {
//               variant: "info",
//               persist: true,
//               action: (id) =>
//                 <IconButton onClick={() => this.props.closeSnackbar(id)} color="inherit">
//                   <CloseIcon />
//                 </IconButton>,
//             });
//           } else {
//             setTimeout(pollServer, 10000);
//           }
//         }).catch((error) =>
//           this.props.enqueueSnackbar(
//             "Project failed to export and the download could not be completed. " +
//             error.message,
//             { variant: "error" },
//           ));
//       }

//       setTimeout(pollServer, 10000);
//     }).catch((error) =>
//       this.props.enqueueSnackbar(
//         "Failed to start project download. " +
//         error.message,
//         { variant: "error" },
//       ));
//   }

//   copyIdToDispInd = (inst, project) => {
//     while (inst.id < project.rootTaskPackage.depthFirstTraversal().length) {
//       inst.displayIndex = inst.id;
//       inst.parentDisplayIndex = inst.parent;
//       if (inst.taskPackages !== undefined && inst.taskPackages.length > 0) {
//         inst.taskPackages.forEach((tp) => {
//           this.copyIdToDispInd(tp, project);
//         });
//       }
//       if (inst.tasks !== undefined && inst.tasks.length > 0) {
//         inst.tasks.forEach((t) => {
//           this.copyIdToDispInd(t, project);
//         });
//       }
//       return;
//     }
//   };

//   redefineTaskAndPkgIds = () => {
//     this.setState({ redefining: true });
//     const projState = this.state.projectStore.toJS();
//     let redefineAPI = "/api/projects/redefine";
//     if (process.env.NODE_ENV !== "production") {
//       redefineAPI = "http://localhost:7071" + redefineAPI;
//     }
//     fetch(redefineAPI, {
//       method: "POST",
//       headers: {
//         Accept: "application/json, text/plain, */*",
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ Projectdata: JSON.stringify(projState) }),
//       redirect: "manual",
//     })
//       .then((response) => {
//         if (!response.ok) {
//           response
//             .text()
//             .then((text) => {
//               throw Error(response.statusText + ": " + text);
//             })
//             .catch((error) =>
//               this.props.enqueueSnackbar(error.message, { variant: "error" })
//             );
//         } else {
//           response.json().then((result) => {
//             const project = result;
//             this.state.projectStore.dispose();
//             this.setState(
//               () => {
//                 return { projectStore: ProjectStore.fromJS(project) };
//               },
//               () => (contentKey = contentKey + 1)
//             );
//             this.setState({ redefiningSuccess: true });
//           });
//         }
//       })
//       .catch((error) =>
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       );
//   };

//   getUserName = () => {
//     let whoamiAPI = "/api/whoami";
//     if (process.env.NODE_ENV !== "production") {
//       whoamiAPI = "http://localhost:7071" + whoamiAPI;
//     }
//     fetch(whoamiAPI, {
//       method: "GET",
//       headers: {
//         Accept: "application/json, text/plain, */*",
//         "Content-Type": "application/json",
//       },
//     }).then((response) => {
//       if (!response.ok) {
//         response
//           .text()
//           .then((text) => {
//             throw Error(response.statusText + ": " + text);
//           })
//           .catch((error) =>
//             this.props.enqueueSnackbar(error.message, { variant: "error" })
//           );
//       } else {
//         response.json().then((result) => {
//           this.setState({ userName: result.userName });
//         });
//       }
//     });
//   };

//   saveNewProjToCloud = () => {
//     if (
//       this.state.projectStore.name === "" ||
//       this.state.projectStore.name == null
//     ) {
//       this.setState({ saveDialogOpen: true });
//       return;
//     }
//     this.setState({ saving: true });
//     const saveState = this.state.projectStore.toJS();
//     let saveNewAPI = "/api/projects/saveNewProj";
//     if (process.env.NODE_ENV !== "production") {
//       saveNewAPI = "http://localhost:7071" + saveNewAPI;
//     }
//     fetch(saveNewAPI, {
//       method: "POST",
//       headers: {
//         Accept: "application/json, text/plain, */*",
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ Projectdata: JSON.stringify(saveState) }),
//       redirect: "manual",
//     })
//       .then((response) => {
//         if (response.type === "opaqueredirect") {
//           this.props.enqueueSnackbar(
//             "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
//             { variant: "error" }
//           );
//           setTimeout(() => {
//             document.location.reload();
//           }, 1000);
//           return;
//         }
//         if (!response.ok) {
//           response
//             .text()
//             .then((text) => {
//               throw Error(response.statusText + ": " + text);
//             })
//             .catch((error) =>
//               this.props.enqueueSnackbar(error.message, { variant: "error" })
//             );
//         } else {
//           response.json().then((result) => {
//             if (result.currentHash !== -1) {
//               let newProject = ProjectStore.fromJS(result);
//               this.setState({ projectStore: newProject });
//             } else {
//               this.setState({ open: true });
//             }
//             this.setState({ saving: false });
//             this.setState({ savingSuccess: true });
//           });
//         }
//       })
//       .catch((error) =>
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       );
//   };

//   saveToCloud = () => {
//     if (
//       this.state.projectStore.name === "" ||
//       this.state.projectStore.name == null
//     ) {
//       this.setState({ saveDialogOpen: true });
//       return;
//     }
//     this.setState({ saving: true });
//     const saveState = this.state.projectStore.toJS();
//     let saveAPI = "/api/projects/save/" + this.state.projectStore.id;
//     if (process.env.NODE_ENV !== "production") {
//       saveAPI = "http://localhost:7071" + saveAPI;
//     }
//     fetch(saveAPI, {
//       method: "POST",
//       headers: {
//         Accept: "application/json, text/plain, */*",
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ Projectdata: JSON.stringify(saveState) }),
//       redirect: "manual",
//     })
//       .then((response) => {
//         if (response.type === "opaqueredirect") {
//           this.props.enqueueSnackbar(
//             "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
//             { variant: "error" }
//           );
//           setTimeout(() => {
//             document.location.reload();
//           }, 1000);
//           return;
//         }
//         if (!response.ok) {
//           response
//             .text()
//             .then((text) => {
//               throw Error(response.statusText + ": " + text);
//             })
//             .catch((error) =>
//               this.props.enqueueSnackbar(error.message, { variant: "error" })
//             );
//         } else {
//           response.json().then((result) => {
//             if (result.currentHash !== -1) {
//               let newProject = ProjectStore.fromJS(result);
//               this.setState({ projectStore: newProject });
//             } else {
//               this.setState({ open: true });
//             }
//             this.setState({ saving: false });
//             this.setState({ savingSuccess: true });
//           });
//         }
//       })
//       .catch((error) =>
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       );
//   };

//   copyToCloud = () => {
//     if (
//       this.state.projectStore.name === "" ||
//       this.state.projectStore.name == null
//     ) {
//       this.setState({ saveDialogOpen: true });
//       return;
//     }
//     this.setState({ saving: true });
//     const saveState = this.state.projectStore.toJS();
//     let saveAPI =
//       "/api/projects/saveAs/" +
//       this.state.projectStore.folderName +
//       "/" +
//       this.state.projectStore.name;
//     if (process.env.NODE_ENV !== "production") {
//       saveAPI = "http://localhost:7071" + saveAPI;
//     }
//     fetch(saveAPI, {
//       method: "POST",
//       headers: {
//         Accept: "application/json, text/plain, */*",
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ Projectdata: JSON.stringify(saveState) }),
//       redirect: "manual",
//     })
//       .then((response) => {
//         if (response.type === "opaqueredirect") {
//           this.props.enqueueSnackbar(
//             "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
//             { variant: "error" }
//           );
//           setTimeout(() => {
//             document.location.reload();
//           }, 1000);
//           return;
//         }
//         if (!response.ok) {
//           response
//             .text()
//             .then((text) => {
//               throw Error(response.statusText + ": " + text);
//             })
//             .catch((error) =>
//               this.props.enqueueSnackbar(error.message, { variant: "error" })
//             );
//         } else {
//           response.json().then((result) => {
//             if (result.currentHash !== -1) {
//               this.setState({ projectStore: this.state.projectStore });
//             } else {
//               this.setState({ open: true });
//             }
//             this.setState({ saving: false });
//             this.setState({ savingSuccess: true });
//           });
//         }
//       })
//       .catch((error) =>
//         this.props.enqueueSnackbar(error.message, { variant: "error" })
//       );
//   };

//   refreshProject = () => {
//     this.loadFromCloud({
//       name: this.state.projectStore.name,
//       folderName: this.state.projectStore.folderName,
//       ownerName: this.state.projectStore.ownerName,
//     });
//   };

//   loadFromCloud = (file) => {
//     this.setState({ refreshing: true });
//     let getAPI =
//       "/api/projects/" +
//       file.folderName +
//       "/file/" +
//       file.name +
//       "/owner/" +
//       file.ownerName;
//     if (process.env.NODE_ENV !== "production") {
//       getAPI = "http://localhost:7071" + getAPI;
//     }
//     fetch(getAPI, {
//       method: "GET",
//       headers: {
//         Accept: "application/json, text/plain, */*",
//         "Content-Type": "application/json",
//       },
//       redirect: "manual",
//     })
//       .then((response) => {
//         if (response.type === "opaqueredirect") {
//           this.props.enqueueSnackbar(
//             "Failed to fetch. Hence the page will be automatically refreshed, and signin again.",
//             { variant: "error" }
//           );
//           setTimeout(() => {
//             document.location.reload();
//           }, 1000);
//           return;
//         }
//         if (!response.ok) {
//           response
//             .text()
//             .then((text) => {
//               throw Error(response.statusText + ": " + text);
//             })
//             .catch((error) =>
//               this.props.enqueueSnackbar(error.message, { variant: "error" })
//             );
//         } else {
//           response.json().then((result) => {
//             const project = result;
//             this.state.projectStore.dispose();
//             this.setState(
//               () => {
//                 return { projectStore: ProjectStore.fromJS(project) };
//               },
//               () => (contentKey = contentKey + 1)
//             );
//             this.setState({ refreshing: false });
//             this.setState({ refreshingSuccess: true });
//           });
//         }
//       })
//       .catch((error) =>
//         this.props.enqueueSnackbar(error.message + { variant: "error" })
//       );
//   };

//   resetState = () => {
//     localStorage.clear();
//     this.state.projectStore.dispose();
//     let newProject = ProjectStore.fromJS(initialProject());
//     this.setState(initialViewState());
//     this.setState(
//       () => {
//         return { projectStore: newProject };
//       },
//       () => {
//         contentKey = contentKey + 1; //force remount of subcomponents that have local state
//       }
//     );
//   };

//   componentDidMount() {
//     checkHash(
//       this.props.projectStore.id,
//       this.props.projectStore.currentHash,
//       this.props.enqueueSnackbar
//     ).catch((error) => {
//       this.props.enqueueSnackbar(error.message, { variant: "error" });
//       if (error.cause === "opaqueredirect") {
//         setTimeout(() => {
//           document.location.reload();
//         }, 1000);
//       }
//     });
//     this.getUserName();
//   }

//   render() {
//     const { classes } = this.props;

//     const projectName =
//       this.state.projectStore.name !== ""
//         ? this.state.projectStore.name
//         : "New Project";

//     return (
//       <div className={classes.root}>
//         <LoadGoogleClient />;
//         <Snackbar
//           open={this.state.open}
//           autoHideDuration={6000}
//           onClose={this.handleClose}
//         >
//           <Alert
//             onClose={this.handleClose}
//             severity="error"
//             elevation={6}
//             variant="filled"
//           >
//             Current project version is out dated, please load most current
//             version of project, changes not saved
//           </Alert>
//         </Snackbar>
//         <Router>
//           <CssBaseline />
//           <AppBar
//             position="absolute"
//             className={classNames(
//               classes.appBar,
//               this.state.mainDrawerOpen && classes.appBarShift
//             )}
//           >
//             <Toolbar
//               disableGutters={!this.state.mainDrawerOpen}
//               className={classes.toolbar}
//             >
//               <Logo
//                 color="inherit"
//                 aria-label="Open drawer"
//                 onClick={this.handleDrawerOpen}
//                 className={classNames(
//                   classes.menuButton,
//                   this.state.mainDrawerOpen && classes.menuButtonHidden
//                 )}
//                 style={{ margin: "0px 30px 0px 15px", height: "30px", width: "30px" }}
//               />
//               <Logo
//                 color="inherit"
//                 className={classNames(
//                   classes.menuButton,
//                   !this.state.mainDrawerOpen && classes.menuButtonHidden
//                 )}
//                 style={{ margin: "0px 30px 0px 0px", height: "30px", width: "30px" }}
//               />
//               <Typography
//                 component="h1"
//                 variant="h6"
//                 color="inherit"
//                 noWrap
//                 className={classes.title}
//               >
//                 {projectName}
//               </Typography>
//               <Tooltip title="New Project" aria-label="New">
//                 <IconButton
//                   onClick={() => {
//                     this.setState({ createNewProjectDialogOpen: true });
//                   }}
//                   color="inherit"
//                 >
//                   <AddIcon />
//                 </IconButton>
//               </Tooltip>
//               <Tooltip title="Open Project" aria-label="Open">
//                 <IconButton
//                   onClick={() => {
//                     this.setState({ projectDialogOpen: true });
//                   }}
//                   color="inherit"
//                 >
//                   <OpenIcon />
//                 </IconButton>
//               </Tooltip>
//               <Tooltip title="Save" aria-label="Save">
//                 <IconButton onClick={() => this.saveToCloud()} color="inherit">
//                   <SaveIcon />
//                 </IconButton>
//               </Tooltip>
//               <Tooltip title="Refresh" aria-label="Refresh">
//                 <IconButton onClick={this.refreshProject} color="inherit">
//                   <RefreshIcon />
//                 </IconButton>
//               </Tooltip>
//               <SplitButton
//                 options={[
//                   {
//                     menuIcon: <NoteAddIcon />,
//                     label: "ExportEVA",
//                     component: <CreateGoogleSheet project={this.state.projectStore} />,
//                     onClick: null,
//                   },
//                   {
//                     menuIcon: <CloudDownloadIcon />,
//                     label: "Download",
//                     component: (
//                       <Tooltip title="Download" aria-label="Download">
//                         <IconButton color="inherit">
//                           <CloudDownloadIcon />
//                         </IconButton>
//                       </Tooltip>
//                     ),
//                     onClick: () => this.handleProjectDownload()
//                   }
//                 ]}
//               />
//               <Tooltip title="About" aria-label="About">
//                 <IconButton
//                   onClick={() => {
//                     this.setState({ aboutDialogOpen: true });
//                   }}
//                   color="inherit"
//                 >
//                   <InfoIcon />
//                 </IconButton>
//               </Tooltip>
//               <Tooltip title="Sign Out" aria-label="Logout">
//                 <Button variant="outlined" href="/.auth/logout" color="inherit">
//                   {this.state.userName}
//                 </Button>
//               </Tooltip>
//               <Snackbar
//                 title="Redefining Started.."
//                 open={this.state.redefining}
//                 autoHideDuration={3000}
//                 onClose={this.handleCloseSnackbar}
//               >
//                 <Alert
//                   onClose={this.handleCloseSnackbar}
//                   severity="info"
//                   sx={{ width: "100%" }}
//                 >
//                   Redefining Packages and Task Ids Started...
//                 </Alert>
//               </Snackbar>
//               <Snackbar
//                 title="Redefine Success"
//                 open={this.state.redefiningSuccess}
//                 autoHideDuration={3000}
//                 onClose={this.handleCloseSnackbar}
//               >
//                 <Alert
//                   onClose={this.handleCloseSnackbar}
//                   severity="success"
//                   sx={{ width: "100%" }}
//                 >
//                   Redefine Successul!!
//                 </Alert>
//               </Snackbar>
//               <Dialog open={this.state.saving}>
//                 <DialogContent style={{ backgroundColor: 'rgb(232, 244, 253)' }}>
//                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                     <CircularProgress size={24} />
//                     <span style={{ marginLeft: 16 }}>Saving ...</span>
//                   </div>
//                 </DialogContent>
//               </Dialog>
//               <Snackbar
//                 title="Saving Successul"
//                 open={this.state.savingSuccess}
//                 autoHideDuration={3000}
//                 onClose={this.handleCloseSnackbar}
//               >
//                 <Alert
//                   onClose={this.handleCloseSnackbar}
//                   severity="success"
//                   sx={{ width: "100%" }}
//                 >
//                   Project is successfully saved!
//                 </Alert>
//               </Snackbar>
//               <Dialog open={this.state.refreshing}>
//                 <DialogContent style={{ backgroundColor: 'rgb(232, 244, 253)' }}>
//                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                     <CircularProgress size={24} />
//                     <span style={{ marginLeft: 16 }}>Refreshing ...</span>
//                   </div>
//                 </DialogContent>
//               </Dialog>
//               <Snackbar
//                 title="Refreshing Successul"
//                 open={this.state.refreshingSuccess}
//                 autoHideDuration={3000}
//                 onClose={this.handleCloseSnackbar}
//               >
//                 <Alert
//                   onClose={this.handleCloseSnackbar}
//                   severity="success"
//                   sx={{ width: "100%" }}
//                 >
//                   The latest project data has been loaded
//                 </Alert>
//               </Snackbar>
//               <DialogCreateNewProject
//                 open={this.state.createNewProjectDialogOpen}
//                 onSave={this.handleCreateNewProjectDialogSave}
//                 onCancel={this.handleCreateNewProjectDialogClose}
//               />
//               <DialogProjectName
//                 open={this.state.saveDialogOpen}
//                 onSave={this.handleSaveProjectName}
//                 onCancel={this.handleSaveDialogClose}
//               />
//               <DialogOpenProject
//                 open={this.state.projectDialogOpen}
//                 onFinish={this.handleOpenProject}
//                 onCancel={this.handleOpenProjectDialogClose}
//               />
//               <DialogAbout
//                 open={this.state.aboutDialogOpen}
//                 onClose={this.handleAboutDialogClose}
//               />
//             </Toolbar>
//           </AppBar>
//           <Drawer
//             variant="permanent"
//             classes={{
//               paper: classNames(
//                 classes.drawerPaper,
//                 !this.state.mainDrawerOpen && classes.drawerPaperClose
//               ),
//             }}
//             open={this.state.mainDrawerOpen}
//           >
//             <div className={classes.toolbarIcon}>
//               <IconButton onClick={this.handleDrawerClose}>
//                 <ChevronLeftIcon />
//               </IconButton>
//             </div>
//             <Divider />
//             <List>{mainListItems}</List>
//           </Drawer>
//           <main className={classes.content}>
//             <div className={classes.appBarSpacer} />
//             <div>
//               <div className={classes.tableContainer}>
//                 <Routes>
//                   <Route
//                     path="/summary"
//                     render={(props) => (
//                       <SummaryView {...props} project={this.state.projectStore} />
//                     )}
//                   />
//                   <Route
//                     path="/tasks"
//                     render={(props) => (
//                       <TaskView
//                         {...props}
//                         project={this.state.projectStore}
//                         view={this.viewState}
//                       />
//                     )}
//                   />
//                   <Route
//                     path="/distributions"
//                     render={(props) => (
//                       <ConfigureDistributionView
//                         {...props}
//                         project={this.state.projectStore}
//                         view={this.viewState}
//                       />
//                     )}
//                   />
//                   <Route
//                     path="/analysis"
//                     render={(props) => (
//                       <AnalysisView
//                         {...props}
//                         project={this.state.projectStore}
//                         view={this.viewState}
//                       />
//                     )}
//                   />
//                   <Route
//                     path="/baselines"
//                     render={(props) => (
//                       <ManageBaselineView
//                         {...props}
//                         project={this.state.projectStore}
//                         userName={this.state.userName}
//                         view={this.viewState}
//                       />
//                     )}
//                   />
//                   <Route
//                     path="/projects"
//                     render={(props) => (
//                       <ManageProjectView
//                         {...props}
//                         project={this.state.projectStore}
//                         userName={this.state.userName}
//                       />
//                     )}
//                   />
//                 </Routes>
//               </div>
//             </div>
//           </main>
//         </Router>
//       </div>
//     );
//   }
// }

// Dashboard.propTypes = {
//   classes: PropTypes.object.isRequired,
// };

// // export default observer(withStyles(styles)(Dashboard));
// // export default observer(withTheme(Dashboard));
// export default withStyles(styles, { withTheme: true })(observer(Dashboard));
